import type { ReactNode, Ref } from "react"
import cx from "clsx"
import { Link } from "wouter"
import styles from "./AdminButton.module.css"

type Variant = "primary" | "outline"

type Common = {
  variant?: Variant
  children: ReactNode
  className?: string
}

/**
 * The admin surface's button, to the form-builder design: 36px tall, lighter
 * and tighter than the kiosk's touch-sized pill in the feedback app's `components/Button`.
 *
 * Used across the admin screens. The two destructive confirm rows — removing a
 * kiosk device, deleting a program question — keep their own CSS until there is
 * a design for a destructive button; each of those rows is styled as a unit, so
 * converting only its non-destructive buttons would leave mismatched pairs.
 */
export function AdminButton({
  variant = "primary",
  // React 19 takes ref as an ordinary prop, so no forwardRef wrapper.
  ref,
  onClick,
  disabled,
  // Unavailable but still focusable and announced, unlike native `disabled`
  // which assistive tech skips. The caller guards its own onClick.
  ariaDisabled,
  describedBy,
  // Names the button for assistive tech when the visible label alone does not
  // say which row it belongs to.
  ariaLabel,
  type = "button",
  children,
  className,
}: Common & {
  ref?: Ref<HTMLButtonElement>
  onClick: () => void
  disabled?: boolean
  ariaDisabled?: boolean
  describedBy?: string
  ariaLabel?: string
  type?: "button" | "submit"
}) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(styles.button, styles[variant], className)}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={ariaDisabled}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  )
}

export function AdminButtonLink({
  variant = "primary",
  href,
  children,
  className,
  ariaLabel,
}: Common & { href: string; ariaLabel?: string }) {
  return (
    <Link
      href={href}
      className={cx(styles.button, styles[variant], className)}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  )
}
